/**
 * Panneaux du configurateur de coffret — périmètre agent 2.
 *
 * Le filtrage est entièrement client : les échantillons sont tous rendus en Liquid, sans
 * pagination, et un aller-retour serveur effacerait la sélection en cours. Ce composant
 * ne fait donc que compter et émettre ; c'est la section qui masque les items.
 *
 * Contrat avec `ax-coffret` : lecture des `data-sample-meta` de `[data-coffret-list]`,
 * émission de `coffret:filters` avec `{ available, values }`, où `values` porte une entrée
 * par groupe déclaré dans `data-groups`.
 */

class CoffretFilters extends HTMLElement {
  #drawer;

  connectedCallback() {
    this.#drawer = this.querySelector('ax-coffret-panel-drawer');

    this.#refreshCounts({ initial: true });

    // Les compteurs suivent la saisie, sans attendre « Appliquer » : c'est en cochant qu'on
    // veut voir ce qui reste possible.
    this.#root.addEventListener('change', (event) => {
      if (event.target.matches('input[type="checkbox"]')) this.#refreshCounts();
    });

    this.querySelector('.ax-coffret-filters__apply')?.addEventListener('click', () => this.#apply());
    this.querySelector('.ax-coffret-filters__reset')?.addEventListener('click', () => this.#reset());

    // Le bouton de l'état vide vit dans la liste, hors de ce host : on l'écoute au niveau du
    // document plutôt que de dupliquer la remise à zéro.
    document.addEventListener('click', (event) => {
      if (event.target.closest('[data-coffret-filters-reset]')) this.#reset();
    });
  }

  /**
   * Racine de recherche des champs. Indispensable : le `Drawer` natif est `shouldAppendToBody`,
   * il se re-parente dans `<body>` à la première ouverture. Passé ce moment, plus rien du
   * panneau n'est descendant du host — `this.querySelectorAll` y renvoie zéro input, et lire
   * les cases cochées depuis `this` produisait un filtre toujours vide.
   * La référence prise au connect, elle, survit au déplacement.
   *
   * L'événement reste émis depuis le host, qui ne bouge pas : c'est ce qui lui permet de
   * remonter jusqu'à la section malgré le déplacement du panneau.
   */
  get #root() {
    return this.#drawer ?? this;
  }

  get #samples() {
    const list = document.querySelector('[data-coffret-list]');
    if (!list) return [];

    return Array.from(list.querySelectorAll('[data-sample-meta]')).flatMap((item) => {
      try {
        return [JSON.parse(item.dataset.sampleMeta)];
      } catch {
        // Un item au JSON illisible ne doit pas priver la page de tous ses compteurs.
        return [];
      }
    });
  }

  /**
   * Un compteur par valeur, recalculé à chaque case cochée — la règle des facettes Shopify :
   * le compte d'une valeur tient compte des AUTRES groupes cochés, jamais du sien, sinon
   * cocher « Les boisés » mettrait toutes les autres familles à 0 alors qu'elles sont en OU.
   *
   * Une valeur que plus rien ne porte est désactivée, pas masquée : la faire disparaître au
   * clic ferait sauter la liste sous le doigt. Seules celles que personne ne porte du tout
   * sont retirées au chargement — elles n'apprennent rien et allongent le panneau.
   */
  #refreshCounts({ initial = false } = {}) {
    const samples = this.#samples;

    for (const control of this.#root.querySelectorAll('.checkbox-control')) {
      const input = control.querySelector('input[type="checkbox"]');
      const output = control.querySelector('[data-coffret-filter-count]');
      if (!input || !output) continue;

      const key = this.#groups.find((group) => group.name === input.name)?.key;
      if (!key) continue;

      const total = samples.filter(
        (sample) => this.#matchesOtherGroups(sample, input.name) && (sample[key] || []).includes(input.value)
      ).length;

      output.textContent = `(${total})`;

      if (initial) control.hidden = total === 0;
      // Une case cochée reste active : il faut pouvoir la décocher pour sortir de l'impasse.
      input.disabled = total === 0 && !input.checked;
    }
  }

  /**
   * L'échantillon passe-t-il les critères des groupes AUTRES que celui qu'on est en train de
   * compter ? La disponibilité, elle, s'applique toujours : ce n'est pas un groupe de valeurs.
   */
  #matchesOtherGroups(sample, exceptName) {
    if (this.#root.querySelector('[data-coffret-filter-available]')?.checked && !sample.available) {
      return false;
    }

    return this.#groups.every(({ name, key }) => {
      if (name === exceptName) return true;

      const checked = this.#checked(name);
      return checked.length === 0 || checked.some((value) => (sample[key] || []).includes(value));
    });
  }

  /**
   * Les groupes de filtres sont déclarés par le Liquid, dans `data-groups`, sous la forme
   * `nom-du-groupe:clé-du-data-sample-meta`. Ajouter un filtre ne touche donc pas ce script.
   */
  get #groups() {
    return (this.dataset.groups || '')
      .split(',')
      .filter(Boolean)
      .map((pair) => {
        const [name, key] = pair.split(':');
        return { name, key };
      });
  }

  #checked(name) {
    return Array.from(this.#root.querySelectorAll(`input[name="${name}"]:checked`)).map((input) => input.value);
  }

  #apply() {
    this.dispatchEvent(
      new CustomEvent('coffret:filters', {
        bubbles: true,
        detail: {
          available: this.#root.querySelector('[data-coffret-filter-available]')?.checked ?? false,
          values: Object.fromEntries(this.#groups.map(({ name, key }) => [key, this.#checked(name)]))
        }
      })
    );

    this.#announce();
    this.#drawer?.hide();
  }

  /**
   * On compte ce qui est RÉELLEMENT visible après coup, plutôt que de rejouer la logique de
   * filtrage : l'annonce reste juste même si la section change sa façon de combiner les
   * critères. `getClientRects()` attrape aussi bien `hidden` qu'un `display: none` de classe.
   */
  #announce() {
    const status = this.querySelector('[data-coffret-filters-status]');
    const list = document.querySelector('[data-coffret-list]');
    if (!status || !list) return;

    const visible = Array.from(list.querySelectorAll('[data-sample-meta]')).filter(
      (item) => item.getClientRects().length > 0
    ).length;

    status.textContent = `${visible} ${status.dataset.label}`;

    document.querySelector('[data-coffret-empty]')?.toggleAttribute('hidden', visible > 0);
  }

  #reset() {
    for (const input of this.#root.querySelectorAll('input[type="checkbox"]')) {
      input.checked = false;
      input.disabled = false;
    }

    this.#refreshCounts();
    this.#apply();
  }
}

if (!window.customElements.get('ax-coffret-filters')) {
  window.customElements.define('ax-coffret-filters', CoffretFilters);
}

/**
 * Drawer des panneaux du coffret : latéral en desktop, bottom sheet en mobile.
 *
 * Le `Drawer` du thème documente `open-from="bottom"` mais ne l'implémente pas — ses deux
 * méthodes d'animation ne calculent que des `translateX`. On étend la classe pour l'axe
 * vertical plutôt que de sortir les panneaux du drawer : on garderait ainsi le piège de
 * focus, l'`inert` sur le reste de la page, la fermeture par Échap et la restitution du
 * focus, qu'il faudrait sinon réécrire.
 *
 * La classe est récupérée dans le registre : ce fichier est un script classique, il ne peut
 * pas importer le module du thème. `whenDefined` évite toute course au chargement.
 */
window.customElements.whenDefined('x-drawer').then(() => {
  const Drawer = window.customElements.get('x-drawer');
  const mobile = window.matchMedia('(max-width: 699px)');

  /**
   * Objet de contrôle minimal attendu par `DialogElement` : il l'utilise comme un thenable,
   * et appelle `cancel()` quand une transition en écrase une autre, `complete()` quand
   * l'animation est désactivée.
   */
  const controls = (animations) => {
    const finished = Promise.all(animations.map((animation) => animation.finished)).catch(() => {});

    return {
      then: (onFulfilled) => finished.then(onFulfilled),
      cancel: () => animations.forEach((animation) => animation.cancel()),
      complete: () => animations.forEach((animation) => animation.finish())
    };
  };

  class CoffretPanelDrawer extends Drawer {
    #slide(opening) {
      const content = this.getShadowPartByName('content');
      const overlay = this.getShadowPartByName('overlay');
      const offsets = opening ? ['translateY(100%)', 'translateY(0)'] : ['translateY(0)', 'translateY(100%)'];

      return controls([
        overlay.animate(
          { opacity: opening ? [0, 1] : [1, 0] },
          { duration: 250, easing: 'ease-in-out', fill: 'forwards' }
        ),
        content.animate(
          { transform: offsets, opacity: opening ? [0, 1] : [1, 0] },
          { duration: 450, easing: 'cubic-bezier(0.86, 0, 0.07, 1)', fill: 'forwards' }
        )
      ]);
    }

    createEnterAnimationControls() {
      return mobile.matches ? this.#slide(true) : super.createEnterAnimationControls();
    }

    createLeaveAnimationControls() {
      return mobile.matches ? this.#slide(false) : super.createLeaveAnimationControls();
    }
  }

  if (!window.customElements.get('ax-coffret-panel-drawer')) {
    window.customElements.define('ax-coffret-panel-drawer', CoffretPanelDrawer);
  }
});

/**
 * Volet « Votre sélection » en bottom sheet tirable, mobile uniquement.
 *
 * Le conteneur `.ax-coffret__panel` appartient à la section : on ne le re-rend pas, on lui
 * ajoute une classe et un déclencheur. Conséquence voulue : sans JS le volet reste en flux
 * sous la liste, ce qui est un repli utilisable, et le sheet n'existe que si le script tourne.
 *
 * Au-dessus de 700px le panneau repasse en `display: contents` et le volet est inerte : la
 * colonne gauche de la section ne doit pas être touchée.
 */
class CoffretSheet {
  #panel;
  #handle;
  #query = window.matchMedia('(max-width: 699px)');
  #start = null;
  #dragged = false;

  constructor(panel) {
    this.#panel = panel;
    this.#handle = this.#createHandle();

    this.#query.addEventListener('change', () => this.#sync());
    this.#sync();

    this.#handle.addEventListener('click', () => this.#toggle());

    /*
     * Le volet s'ouvre et se referme d'un appui n'importe où sur son en-tête — poignée,
     * vignette, ligne « Votre sélection », jauge. Viser la seule ligne du titre ne suffisait
     * pas : elle ne fait que 15px de haut, et le `details` qui la porte est ouvert de force en
     * mobile avec sa liste sortie (cf. ax-coffret.js), si bien qu'un appui à côté ne faisait
     * rien. Une fois ouvert, le contenu garde ses appuis : la sélection, la suggestion et le
     * bon d'achat se lisent sans que le volet se referme sous le doigt. Les contrôles, eux,
     * gardent leur geste dans les deux états.
     */
    this.#panel.addEventListener('click', (event) => {
      if (!this.#query.matches) return;

      // Un tirage émet aussi un `click`, sur l'ancêtre commun du départ et de l'arrivée : sans
      // cette sortie, il rouvrait aussitôt le volet qu'on venait de refermer en le poussant.
      if (this.#dragged) {
        this.#dragged = false;
        return;
      }

      if (event.target.closest('button, a, input, label, select, textarea')) return;
      if (this.#open && event.target.closest('.ax-coffret__selection, .ax-coffret__suggestion, .ax-coffret__bonus')) return;

      event.preventDefault();
      this.#toggle();
    });

    this.#panel.addEventListener('pointerdown', (event) => this.#onPointerDown(event));

    /*
     * Le relâchement est écouté sur la fenêtre, pas sur le volet : à la souris — donc dans
     * l'émulation mobile utilisée en recette — `pointerup` est délivré à l'élément survolé,
     * et tirer le volet au-delà de son bord faisait perdre le geste. Le tactile, lui, capture
     * implicitement le pointeur et revenait toujours au volet.
     */
    window.addEventListener('pointerup', (event) => this.#onPointerUp(event));
  }

  /**
   * La poignée est un vrai bouton : c'est le seul moyen d'ouvrir le volet au clavier, le
   * tirage étant réservé au pointeur. Le trait de la maquette est dessiné en CSS.
   */
  #createHandle() {
    const handle = document.createElement('button');
    handle.type = 'button';
    handle.className = 'ax-coffret-sheet__handle';
    handle.setAttribute('aria-expanded', 'false');
    handle.innerHTML = `<span class="sr-only">${this.#panel.dataset.sheetLabel || ''}</span>`;

    this.#panel.prepend(handle);
    return handle;
  }

  get #open() {
    return this.#panel.classList.contains('ax-coffret-sheet--open');
  }

  #sync() {
    this.#panel.classList.toggle('ax-coffret-sheet', this.#query.matches);
    this.#setOpen(false);
  }

  #setOpen(open) {
    if (open) this.#measureLine();

    this.#panel.classList.toggle('ax-coffret-sheet--open', open);
    this.#handle.setAttribute('aria-expanded', String(open));
  }

  /**
   * La hauteur d'une ligne est mesurée au lieu d'être écrite en dur : c'est la section qui
   * rend ces lignes, leur gabarit peut changer sans que ce fichier le sache. Sert à plafonner
   * la liste à 5 lignes avant défilement.
   */
  #measureLine() {
    const line = this.#panel.querySelector('[data-coffret-selection] > *');
    if (!line) return;

    const height = line.getBoundingClientRect().height;
    if (height > 0) this.#panel.style.setProperty('--ax-coffret-sheet-line', `${height}px`);
  }

  #toggle() {
    this.#setOpen(!this.#open);
  }

  #onPointerDown(event) {
    if (!this.#query.matches) return;
    this.#start = event.clientY;
    this.#dragged = false;
  }

  /**
   * Un geste vertical de plus de 30px ouvre ou ferme ; en dessous c'est un clic, et on ne
   * bascule que si l'utilisateur a visé la poignée — sinon un appui sur le CTA fermerait
   * le volet au lieu d'ajouter au panier.
   */
  #onPointerUp(event) {
    if (!this.#query.matches || this.#start === null) return;

    const travel = this.#start - event.clientY;
    this.#start = null;

    if (Math.abs(travel) > 30) {
      this.#dragged = true;
      this.#setOpen(travel > 0);
    }
  }
}

const initSheet = () => {
  const panel = document.querySelector('.ax-coffret__panel');
  if (panel && !panel.dataset.sheetReady) {
    panel.dataset.sheetReady = 'true';
    new CoffretSheet(panel);
  }
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initSheet, { once: true });
} else {
  initSheet();
}
